/**
 * Littlebird Quick Wins: Scenario Finder
 *
 * The guide document is unchanged; only its wrapper is. It used to carry
 * its own 49px strip with a back link and no footer, so a visitor who landed
 * here from an email had no wordmark, no account control and no way into the
 * rest of the site. GuideShell supplies all three, and GuideFrame lets the
 * document size itself so the footer sits after it rather than behind a
 * second scrollbar.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HTML_SRC = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Littlebird Quick Wins: Scenario Finder | Your Smart Stack</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --cream: oklch(0.98 0.008 85);
    --linen: oklch(0.93 0.01 85);
    --espresso: oklch(0.22 0.015 65);
    --terracotta: oklch(0.32 0.08 255);
    --border: oklch(0.88 0.012 85);
    --white: oklch(1 0.004 85);
    --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22);
    --muted: oklch(0.52 0.015 65);
    --body: oklch(0.52 0.015 65);
  }

  body {
    background: var(--cream);
    color: var(--espresso);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    min-height: 100vh;
    padding: 40px 20px 60px;
  }

  .container { max-width: 680px; margin: 0 auto; }

  /* HEADER */
  .header {
    text-align: center;
    margin-bottom: 48px;
    padding-bottom: 36px;
    border-bottom: 1px solid var(--border);
  }

  .header .eyebrow {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 14px;
  }

  .header h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(28px, 5vw, 38px);
    font-weight: 700;
    color: var(--espresso);
    line-height: 1.2;
    margin-bottom: 14px;
  }

  .header p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto;
  }

  /* SCREEN MANAGEMENT */
  .screen { display: none; }
  .screen.active { display: block; }

  /* SCENARIO GRID */
  .scenario-label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
    text-align: center;
    margin-bottom: 20px;
  }

  .scenario-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 32px;
  }

  @media (max-width: 480px) {
    .scenario-grid { grid-template-columns: 1fr; }
  }

  .scenario-card {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 3px;
    padding: 20px 20px 18px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .scenario-card:hover {
    border-color: var(--terracotta-mid);
    background: var(--terracotta-light);
    transform: translateY(-1px);
  }

  .scenario-icon {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    color: var(--terracotta);
  }
  .scenario-icon svg {
    width: 28px;
    height: 28px;
    display: block;
  }

  .scenario-moment {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--terracotta);
  }

  .scenario-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--espresso);
    line-height: 1.3;
  }

  .scenario-hint {
    font-size: 14px;
    color: var(--muted);
    line-height: 1.5;
  }

  /* RESULT SCREEN */
  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--muted);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin-bottom: 28px;
    transition: color 0.2s ease;
  }

  .back-btn:hover { color: var(--terracotta); }

  .result-eyebrow {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 8px;
  }

  .result-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(22px, 4vw, 30px);
    font-weight: 700;
    color: var(--espresso);
    line-height: 1.2;
    margin-bottom: 28px;
    padding-bottom: 24px;
    border-bottom: 1px solid var(--border);
  }

  /* WIN CARDS */
  .win-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 3px;
    margin-bottom: 16px;
    overflow: hidden;
  }

  .win-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 22px;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s ease;
  }

  .win-header:hover { background: var(--terracotta-light); }

  .win-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    color: var(--terracotta);
  }
  .win-icon svg { width: 22px; height: 22px; display: block; }

  .win-title-wrap { flex: 1; }

  .win-label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 3px;
  }

  .win-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--espresso);
    line-height: 1.3;
  }

  .win-toggle {
    font-size: 20px;
    color: var(--border);
    flex-shrink: 0;
    transition: transform 0.25s ease, color 0.25s ease;
    line-height: 1;
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  }

  .win-card.open .win-toggle { transform: rotate(45deg); color: var(--terracotta); }

  .win-body {
    display: none;
    padding: 4px 22px 22px 56px;
    border-top: 1px solid var(--border);
  }

  .win-card.open .win-body { display: block; }

  .win-body p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin: 14px 0 12px;
  }

  .win-body ul {
    list-style: none;
    margin-bottom: 16px;
  }

  .win-body ul li {
    font-size: 14px;
    line-height: 1.6;
    color: var(--body);
    padding: 5px 0 5px 18px;
    position: relative;
    border-bottom: 1px solid var(--border);
  }

  .win-body ul li:last-child { border-bottom: none; }

  .win-body ul li::before {
    content: "—";
    position: absolute;
    left: 0;
    color: var(--terracotta);
    font-weight: 300;
  }

  /* PRINCIPLE BOX */
  .principle-box {
    background: var(--linen);
    border-left: 3px solid var(--terracotta);
    border-radius: 0 3px 3px 0;
    padding: 12px 16px;
    margin: 4px 0 8px;
  }

  .principle-box .label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 5px;
  }

  .principle-box p {
    font-size: 14px;
    color: var(--espresso);
    line-height: 1.6;
    margin: 0;
    font-style: italic;
  }

  /* OTHER WINS */
  .other-wins {
    margin-top: 32px;
    padding-top: 28px;
    border-top: 1px solid var(--border);
  }

  .other-label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 14px;
  }

  .other-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .other-pill {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 500;
    padding: 8px 16px;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 2px;
    color: var(--body);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .other-pill:hover {
    border-color: var(--terracotta-mid);
    background: var(--terracotta-light);
    color: var(--espresso);
  }

  /* TIP BOX */
  .tip-box {
    background: var(--linen);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 22px 26px;
    margin-top: 32px;
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .tip-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--terracotta);
  }
  .tip-icon svg { width: 22px; height: 22px; display: block; }

  .tip-box p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin: 0;
  }

  /* FOOTER */
  .footer {
    text-align: center;
    margin-top: 48px;
    padding-top: 28px;
    border-top: 1px solid var(--border);
  }

  .footer p {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.88 0.012 85);
    letter-spacing: 0.04em;
  }

  .footer a { color: var(--terracotta); text-decoration: none; }
</style>
</head>
<body>
<div class="container">

  <!-- HEADER -->
  <div class="header">
    <p class="eyebrow">Resource Center: Smart Stack</p>
    <h1>Littlebird<br>Quick Wins Finder</h1>
    <p>Pick the moment you're in right now. Get exactly the Quick Win that fits.</p>
  </div>

  <!-- SCREEN: PICKER -->
  <div class="screen active" id="screen-picker">

    <div class="scenario-label">What's happening right now?</div>

    <div class="scenario-grid">

      <div class="scenario-card" onclick="showResult('meeting-before')">
        <div class="scenario-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/></svg></div>
        <div class="scenario-moment">Right now</div>
        <div class="scenario-title">I have a meeting coming up</div>
        <div class="scenario-hint">I want to walk in prepared</div>
      </div>

      <div class="scenario-card" onclick="showResult('meeting-after')">
        <div class="scenario-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-5"/></svg></div>
        <div class="scenario-moment">Just finished</div>
        <div class="scenario-title">I just got out of a meeting</div>
        <div class="scenario-hint">I need to follow up and capture what happened</div>
      </div>

      <div class="scenario-card" onclick="showResult('deep-work')">
        <div class="scenario-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="14" rx="2"/><path d="M2 20h20"/><path d="M8 17h8"/></svg></div>
        <div class="scenario-moment">Mid-task</div>
        <div class="scenario-title">I'm in the middle of work</div>
        <div class="scenario-hint">I need a quick answer without losing my flow</div>
      </div>

      <div class="scenario-card" onclick="showResult('end-of-week')">
        <div class="scenario-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 20h18M8 20V10M12 20V6M16 20v-4"/></svg></div>
        <div class="scenario-moment">End of week</div>
        <div class="scenario-title">I want to review my week</div>
        <div class="scenario-hint">I'm not sure where my time went</div>
      </div>

      <div class="scenario-card" onclick="showResult('just-installed')">
        <div class="scenario-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3C7 3 4 7 4 11c0 2.5 1.5 4.5 3 5.5V20h10v-3.5c1.5-1 3-3 3-5.5 0-4-3-8-8-8z"/><path d="M9 20v1m6-1v1"/><path d="M9 11c0-1.5 1.5-3 3-3s3 1.5 3 3"/></svg></div>
        <div class="scenario-moment">Getting started</div>
        <div class="scenario-title">I just installed Littlebird</div>
        <div class="scenario-hint">I want to set it up right from the start</div>
      </div>

      <div class="scenario-card" onclick="showResult('privacy')">
        <div class="scenario-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg></div>
        <div class="scenario-moment">Concerned</div>
        <div class="scenario-title">I want to control what it sees</div>
        <div class="scenario-hint">I need to set boundaries before going further</div>
      </div>

      <div class="scenario-card" onclick="showResult('recurring')">
        <div class="scenario-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12a8 8 0 0 1 14.5-4.5L20 9M4 12l1.5-1.5A8 8 0 0 0 20 12"/><path d="M20 12a8 8 0 0 1-14.5 4.5L4 15M20 12l-1.5 1.5A8 8 0 0 0 4 12"/></svg></div>
        <div class="scenario-moment">Recurring friction</div>
        <div class="scenario-title">I keep doing the same task manually</div>
        <div class="scenario-hint">I want to automate something that repeats</div>
      </div>

      <div class="scenario-card" onclick="showResult('not-using-it')">
        <div class="scenario-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 9h.01M15 9h.01"/><path d="M9 15h6"/></svg></div>
        <div class="scenario-moment">Stuck</div>
        <div class="scenario-title">I installed it but I'm not really using it</div>
        <div class="scenario-hint">I'm not sure what to ask or when to open it</div>
      </div>

    </div>

    <div class="tip-box">
      <div class="tip-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3C7 3 4 7 4 11c0 2.5 1.5 4.5 3 5.5V20h10v-3.5c1.5-1 3-3 3-5.5 0-4-3-8-8-8z"/><path d="M9 20v1m6-1v1"/><path d="M9 11c0-1.5 1.5-3 3-3s3 1.5 3 3"/></svg></div>
      <p><strong>How to use this:</strong> Pick the situation you're actually in right now. Don't read everything. Just get the win that fits this moment. That's the whole point.</p>
    </div>

  </div>

  <!-- SCREEN: RESULT -->
  <div class="screen" id="screen-result">

    <button class="back-btn" onclick="goBack()">← All scenarios</button>

    <div id="result-content"></div>

    <div class="other-wins">
      <div class="other-label">Other scenarios</div>
      <div class="other-grid" id="other-grid"></div>
    </div>

  </div>

  <!-- FOOTER -->

</div>

<script>

const scenarios = {
  'meeting-before': {
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/></svg>',
    moment: 'Right now',
    title: 'I have a meeting coming up',
    wins: [
      {
        id: 'qw3',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><path d="M8 14h.01M12 14h.01"/></svg>',
        label: 'Quick Win #3',
        title: 'Prepare for Any Meeting',
        body: 'Before the meeting, open Littlebird and ask it to research who you\\'re meeting. It will pull from your email history, their public background, and your calendar notes.',
        steps: [
          'Open Littlebird right now, before the meeting',
          'Ask: "Who am I meeting with [name]? Give me background and context"',
          'Ask: "Give me 3–5 talking points and one collaboration idea"',
          'Review for two minutes, then walk in'
        ],
        principle: 'Preparation shouldn\\'t cost you the time it saves.'
      }
    ]
  },

  'meeting-after': {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-5"/></svg>',
    moment: 'Just finished',
    title: 'I just got out of a meeting',
    wins: [
      {
        id: 'qw4',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></svg>',
        label: 'Quick Win #4',
        title: 'Capture and Close the Loop',
        body: 'Littlebird was running during your meeting. It already has the transcript, so now use it to close the loop in under five minutes.',
        steps: [
          'Open Littlebird immediately after the meeting',
          'Ask: "Draft a follow-up email based on what we just discussed"',
          'Ask: "List my commitments and action items from this meeting"',
          'Review, edit lightly, and send. Done.'
        ],
        principle: 'The loop that closes itself removes the task you always forget.'
      }
    ]
  },

  'deep-work': {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="14" rx="2"/><path d="M2 20h20M8 17h8"/></svg>',
    moment: 'Mid-task',
    title: 'I\\'m in the middle of work',
    wins: [
      {
        id: 'qw7',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></svg>',
        label: 'Quick Win #7',
        title: 'Use Hummingbird for In-Context Help',
        body: 'You don\\'t need to switch tabs or lose your place. Hummingbird opens a floating window over whatever you\\'re working on: ask your question, get your answer, close it, keep going.',
        steps: [
          'Press the Hummingbird keyboard shortcut (set in Littlebird settings)',
          'A floating window opens over your current app',
          'Ask your question. Littlebird uses context it already has',
          'Close it and keep working. No momentum lost.'
        ],
        principle: 'The best tool is the one that doesn\\'t interrupt the work.'
      }
    ]
  },

  'end-of-week': {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 20h18M8 20V10M12 20V6M16 20v-4"/></svg>',
    moment: 'End of week',
    title: 'I want to review my week',
    wins: [
      {
        id: 'qw5',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="9" rx="7" ry="9"/><path d="M12 18v4M9 22h6"/></svg>',
        label: 'Quick Win #5',
        title: 'Ask for Your Weekly Productivity Mirror',
        body: 'Littlebird has been watching all week. You don\\'t have to remember anything. Just ask. Read it without judgment and decide what to change.',
        steps: [
          'Ask: "Summarize what I worked on this week"',
          'Ask: "Which areas got the most time, and which got the least?"',
          'Ask: "What\\'s one thing I should streamline or improve next week?"',
          'Read it without judgment, then decide what to change'
        ],
        principle: 'You can\\'t improve what you can\\'t see clearly.'
      }
    ]
  },

  'just-installed': {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3C7 3 4 7 4 11c0 2.5 1.5 4.5 3 5.5V20h10v-3.5c1.5-1 3-3 3-5.5 0-4-3-8-8-8z"/><path d="M9 20v1m6-1v1"/><path d="M9 11c0-1.5 1.5-3 3-3s3 1.5 3 3"/></svg>',
    moment: 'Getting started',
    title: 'I just installed Littlebird',
    wins: [
      {
        id: 'qw1',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21h6M12 3a6 6 0 0 1 4.5 10c-.7.8-1.5 1.5-1.5 2.5V18H9v-2.5c0-1-.8-1.7-1.5-2.5A6 6 0 0 1 12 3z"/></svg>',
        label: 'Quick Win #1',
        title: 'Know What Littlebird Is (and Isn\\'t)',
        body: 'Before you try to use it, understand what makes it different. Littlebird isn\\'t a chatbot you prompt. It\\'s a thinking partner that already knows your work.',
        steps: [
          'Littlebird has a full chat interface, but it already knows your work',
          'You don\\'t paste context or explain your projects. It\\'s already there',
          'Think of it less as a chatbot, more as a thinking partner with a full memory of your day',
          'The chat gets smarter over time as it learns how you work'
        ],
        principle: 'Context changes everything, even in a chat window.'
      },
      {
        id: 'qw2',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3h14M5 21h14M7 3v5l5 4-5 4v5M17 3v5l-5 4 5 4v5"/></svg>',
        label: 'Quick Win #2',
        title: 'Install and Let It Learn',
        body: 'Resist the urge to immediately put it to work. The best thing you can do on day one is let it observe.',
        steps: [
          'Download at littlebird.ai (free to start, no invite required)',
          'Let it run in the background while you work normally today',
          'Connect your calendar and email for deeper context (optional but recommended)',
          'Don\\'t ask it anything heavy yet. Give it a full workday first'
        ],
        principle: 'Patience in setup pays back in relevance.'
      }
    ]
  },

  'privacy': {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>',
    moment: 'Concerned',
    title: 'I want to control what it sees',
    wins: [
      {
        id: 'qw9',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>',
        label: 'Quick Win #9',
        title: 'Control What Littlebird Sees',
        body: 'Always-on doesn\\'t mean no boundaries. You have full control over what Littlebird observes, and you can change it any time.',
        steps: [
          'Open settings → choose which apps Littlebird observes and which it ignores',
          'Pause context collection anytime with one click. It stops immediately',
          'Delete recent data (last hour, last day, or all of it) whenever you choose',
          'Littlebird never sees minimized apps, private browser windows, or password fields'
        ],
        principle: 'Trust requires transparency and the ability to say stop.'
      }
    ]
  },

  'recurring': {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12a8 8 0 0 1 14.5-4.5L20 9M4 12l1.5-1.5A8 8 0 0 0 20 12"/><path d="M20 12a8 8 0 0 1-14.5 4.5L4 15M20 12l-1.5 1.5A8 8 0 0 0 4 12"/></svg>',
    moment: 'Recurring friction',
    title: 'I keep doing the same task manually',
    wins: [
      {
        id: 'qw6',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>',
        label: 'Quick Win #6',
        title: 'Build a Routine Once, Run It Forever',
        body: 'If a task repeats (daily briefing, inbox triage, weekly summary), Littlebird can run it on a schedule so you never have to think about it again.',
        steps: [
          'Open Routines in Littlebird',
          'Create a scheduled task: daily, weekly, or monthly',
          'Start with one: a daily briefing, an inbox triage, or a weekly summary',
          'Set it, confirm the schedule, and let Littlebird handle it from there'
        ],
        principle: 'Automation earns you back the time to think.'
      }
    ]
  },

  'not-using-it': {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 9h.01M15 9h.01M9 15h6"/></svg>',
    moment: 'Stuck',
    title: 'I installed it but I\\'m not really using it',
    wins: [
      {
        id: 'qw8',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20v-6M8 14a4 4 0 0 0 8 0V8H8v6z"/><path d="M10 4v4M14 4v4"/></svg>',
        label: 'Quick Win #8',
        title: 'Connect Your Tools for Deeper Context',
        body: 'If Littlebird\\'s answers feel surface-level, it\\'s probably because it doesn\\'t have enough signal yet. Two integrations make a dramatic difference.',
        steps: [
          'Go to integrations: connect Gmail and Google Calendar first',
          'Littlebird supports 90+ integrations, but you don\\'t need all of them',
          'Email and calendar alone dramatically improve context quality',
          'Integrations are optional. Littlebird works without them via screen reading alone'
        ],
        principle: 'More signal, less noise. Connect only what you\\'ll actually use.'
      },
      {
        id: 'qw10',
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1" fill="currentColor"/></svg>',
        label: 'Quick Win #10',
        title: 'Let Littlebird Run While You Focus Elsewhere',
        body: 'The mistake most people make is trying to manage Littlebird like a tool they have to use. Stop managing it. Just reach for it when you need it.',
        steps: [
          'Stop checking it daily. That\\'s not how it\\'s meant to be used',
          'Next time you need context on something, open it and ask',
          'Trust that it\\'s been building memory while you focused on other things',
          'Use it like memory, not a filing system. Query it when you need it'
        ],
        principle: 'The tools that ask the least often give the most back.'
      }
    ]
  }
};

const scenarioLabels = {
  'meeting-before': 'Meeting coming up',
  'meeting-after': 'Just out of a meeting',
  'deep-work': 'In the middle of work',
  'end-of-week': 'End of week review',
  'just-installed': 'Just installed',
  'privacy': 'Control what it sees',
  'recurring': 'Repeating tasks',
  'not-using-it': 'Not really using it'
};

let currentScenario = null;
let openWin = null;

function showResult(key) {
  currentScenario = key;
  const data = scenarios[key];

  // Build wins HTML
  let winsHTML = \`
    <div class="result-eyebrow">\${data.moment}</div>
    <div class="result-title">\${data.title}</div>
  \`;

  data.wins.forEach((win, i) => {
    const isFirst = i === 0;
    winsHTML += \`
      <div class="win-card \${isFirst ? 'open' : ''}" id="win-\${win.id}">
        <div class="win-header" onclick="toggleWin('win-\${win.id}')">
           <span class="win-icon">\${win.icon}</span>
          <div class="win-title-wrap">
            <div class="win-label">\${win.label}</div>
            <div class="win-title">\${win.title}</div>
          </div>
          <span class="win-toggle">+</span>
        </div>
        <div class="win-body">
          <p>\${win.body}</p>
          <ul>
            \${win.steps.map(s => \`<li>\${s}</li>\`).join('')}
          </ul>
          <div class="principle-box">
            <div class="label">Smart Stack Principle</div>
            <p>\${win.principle}</p>
          </div>
        </div>
      </div>
    \`;
  });

  document.getElementById('result-content').innerHTML = winsHTML;

  // Build other scenarios pills
  const otherGrid = document.getElementById('other-grid');
  otherGrid.innerHTML = '';
  Object.keys(scenarios).forEach(k => {
    if (k !== key) {
      const pill = document.createElement('button');
      pill.className = 'other-pill';
      pill.textContent = scenarioLabels[k];
      pill.onclick = () => showResult(k);
      otherGrid.appendChild(pill);
    }
  });

  // Switch screens
  document.getElementById('screen-picker').classList.remove('active');
  document.getElementById('screen-result').classList.add('active');
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function goBack() {
  document.getElementById('screen-result').classList.remove('active');
  document.getElementById('screen-picker').classList.add('active');
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function toggleWin(id) {
  const card = document.getElementById(id);
  card.classList.toggle('open');
}

</script>
</body>
</html>
`;

export default function LittlebirdScenarioFinder() {
  return (
    <GuideShell
      context={"Littlebird guide"}
      backHref={"/resources/littlebird"}
      backLabel={"Back to Littlebird"}
    >
      <GuideFrame html={HTML_SRC} title={"Littlebird Quick Wins: Scenario Finder"} />
    </GuideShell>
  );
}
